import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  // Globe, Users, Calendar, MapPin, // TODO: Add these icons to enhanced company profile view
  Save,
  Loader2,
  Camera,
  Award,
  Check
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { Navigation } from '../../components/Navigation';

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Manufacturing',
  'Consulting',
  'Real Estate',
  'Marketing',
  'Legal',
  'Media',
  'Hospitality',
  'Transportation',
  'Energy',
  'Other',
];

const COMPANY_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1001-5000 employees',
  '5000+ employees',
];

const COMMON_BENEFITS = [
  'Health Insurance',
  'Dental Insurance',
  'Vision Insurance',
  '401(k)',
  'Paid Time Off',
  'Remote Work',
  'Flexible Hours',
  'Stock Options',
  'Professional Development',
  'Gym Membership',
  'Commuter Benefits',
  'Life Insurance',
  'Parental Leave',
  'Tuition Reimbursement',
];

export function CompanyProfile() {
  const { user, profile } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    website: '',
    location: '',
    headquarters: '',
    avatar_url: '',
    industry: '',
    company_size: '',
    founded_year: '',
    bio: '',
    benefits: [] as string[],
    custom_benefit: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (profile?.role !== 'employer') {
      navigate('/');
      return;
    }

    if (profile) {
      setFormData({
        company_name: profile.company_name || '',
        email: profile.email || '',
        phone: (profile as any).phone || '',
        website: (profile as any).website || '',
        location: profile.location || '',
        headquarters: (profile as any).headquarters || '',
        avatar_url: profile.avatar_url || '',
        industry: (profile as any).industry || '',
        company_size: (profile as any).company_size || '',
        founded_year: (profile as any).founded_year || '',
        bio: profile.bio || '',
        benefits: (profile as any).benefits || [],
        custom_benefit: '',
      });
    }
  }, [user, profile, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const updates: any = {
        company_name: formData.company_name,
        email: formData.email,
        phone: formData.phone || null,
        website: formData.website || null,
        location: formData.location || null,
        headquarters: formData.headquarters || null,
        avatar_url: formData.avatar_url || null,
        industry: formData.industry || null,
        company_size: formData.company_size || null,
        founded_year: formData.founded_year || null,
        bio: formData.bio || null,
        benefits: formData.benefits.length > 0 ? formData.benefits : null,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user!.id);

      if (updateError) {
        console.error('Profile update error:', updateError);
        setError('Failed to update company profile. Please try again.');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const toggleBenefit = (benefit: string) => {
    if (formData.benefits.includes(benefit)) {
      setFormData({
        ...formData,
        benefits: formData.benefits.filter((b) => b !== benefit),
      });
    } else {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, benefit],
      });
    }
  };

  const addCustomBenefit = () => {
    const benefit = formData.custom_benefit.trim();
    if (benefit && !formData.benefits.includes(benefit)) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, benefit],
        custom_benefit: '',
      });
    }
  };

  const removeBenefit = (benefit: string) => {
    setFormData({
      ...formData,
      benefits: formData.benefits.filter((b) => b !== benefit),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Company Profile</h1>
          <p className="text-gray-600">Manage your company information and attract top talent</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700 font-medium">Company profile updated successfully!</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Company Information */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
            </div>

            <div className="space-y-6">
              {/* Company Logo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Logo
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden">
                    {formData.avatar_url ? (
                      <img src={formData.avatar_url} alt="Company Logo" className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition"
                    >
                      <Camera className="w-4 h-4" />
                      Upload Logo
                    </button>
                    <p className="text-xs text-gray-500 mt-2">JPG, PNG or SVG. Max 2MB.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company_name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="Acme Inc."
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="contact@acme.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-semibold text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="https://acme.com"
                  />
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
                    Industry
                  </label>
                  <select
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                  >
                    <option value="">Select industry</option>
                    {INDUSTRIES.map((industry) => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="company_size" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Size
                  </label>
                  <select
                    id="company_size"
                    value={formData.company_size}
                    onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                  >
                    <option value="">Select size</option>
                    {COMPANY_SIZES.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="founded_year" className="block text-sm font-semibold text-gray-700 mb-2">
                    Founded Year
                  </label>
                  <input
                    type="number"
                    id="founded_year"
                    min="1800"
                    max={new Date().getFullYear()}
                    value={formData.founded_year}
                    onChange={(e) => setFormData({ ...formData, founded_year: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="2020"
                  />
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="San Francisco, CA"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Description
                </label>
                <textarea
                  id="bio"
                  rows={5}
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition resize-none"
                  placeholder="Tell job seekers about your company, culture, mission, and what makes you a great place to work..."
                />
              </div>
            </div>
          </div>

          {/* Benefits & Perks */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-gray-900">Benefits & Perks</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Benefits
                </label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {COMMON_BENEFITS.map((benefit) => (
                    <button
                      key={benefit}
                      type="button"
                      onClick={() => toggleBenefit(benefit)}
                      className={`px-4 py-2 rounded-full font-medium text-sm transition ${
                        formData.benefits.includes(benefit)
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {benefit}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="custom_benefit" className="block text-sm font-semibold text-gray-700 mb-2">
                  Add Custom Benefit
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="custom_benefit"
                    value={formData.custom_benefit}
                    onChange={(e) => setFormData({ ...formData, custom_benefit: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomBenefit();
                      }
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="Type a benefit and press Enter"
                  />
                  <button
                    type="button"
                    onClick={addCustomBenefit}
                    className="px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition"
                  >
                    Add
                  </button>
                </div>
              </div>

              {formData.benefits.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Selected Benefits ({formData.benefits.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {formData.benefits.map((benefit) => (
                      <span
                        key={benefit}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm font-medium"
                      >
                        {benefit}
                        <button
                          type="button"
                          onClick={() => removeBenefit(benefit)}
                          className="hover:text-orange-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Profile
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
